import {
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  SimpleChanges,
  viewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { BehaviorSubject, distinctUntilChanged, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { FieldTypeEnum, RestrictionType, RestrictionValueType } from '@models/module/fields/enums';
import { DataListFieldPropertiesModel, ScreenFieldModel } from '@models/module/fields/main';
import { GridSearchEntityInstance, GridViewModel, SearchViewModel } from '@models/module/grid';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { Currency, CurrencyFn } from '@models/system';
import { TableFieldModel } from '@module-definition/table-field/common/table-field.models';
import { CalendarDialogDocDetailPrefix } from '@module-instance/calendar/calendar-dialog-doc-detail/calendar-dialog-doc-detail.component';
import {
  CalendarDialogMoreComponent,
  CalendarDialogMoreOuterHeight,
  CalendarDialogMoreOuterWidth,
  CalendarDialogMorePrefix,
} from '@module-instance/calendar/calendar-dialog-more/calendar-dialog-more.component';
import {
  CalendarDate,
  CalendarReferenceField,
  CalendarTile,
  CalendarTileDoc,
  CalendarViewMode,
} from '@module-instance/calendar/calendar.model';
import { CalendarService } from '@module-instance/calendar/calendar.service';
import { FieldDefinitionService } from '@module-instance/services';
import { TableDataService } from '@shared/components/data-list/common';
import { PRESET_BG_COLOR } from '@shared/consts';
import { ItvDateTimeWithRestrictionsPipe } from '@shared/pipes/itv-date.pipe';
import { ItvNumberWithFormattingsPipe } from '@shared/pipes/itv-number.pipe';
import { LocaleDateAdapter, LocaleService } from '@shared/services';
import { parseTableFieldValue } from '@shared/utils';

/** Minimal column shape needed to render/format Table and Weather values; Weather columns are
 *  fixed and known upfront (no server fetch needed), unlike Table columns. */
type DescTableColumn = Pick<TableFieldModel, 'label' | 'name' | 'type' | 'formattings' | 'restrictions'>;

@Component({
  selector: 'app-calendar-base-view',
  template: '<p>calendar base view</p>',
  host: {
    '(window:resize)': 'onResize()',
  },
})
export abstract class CalendarBaseViewComponent implements OnChanges, OnDestroy {
  protected readonly localeService = inject(LocaleService);
  protected readonly dateAdapter = inject(LocaleDateAdapter);
  protected readonly calendarService = inject(CalendarService);
  protected readonly dialog = inject(MatDialog);
  protected readonly itvDateTimePipe = inject(ItvDateTimeWithRestrictionsPipe);
  protected readonly itvNumberPipe = inject(ItvNumberWithFormattingsPipe);
  protected readonly tableDataService = inject(TableDataService);
  private readonly fieldDefinitionService = inject(FieldDefinitionService);

  readonly date = input<CalendarDate>();
  readonly dateField = input<CalendarReferenceField>();
  readonly descField = input<CalendarReferenceField>();
  readonly actions = input<DataListViewAction[]>();
  readonly search = input<SearchViewModel>();
  readonly contractId = input<number>();
  readonly screenFields = input<ScreenFieldModel[]>();
  readonly currency = input<Currency | CurrencyFn>();

  readonly onLoading = output<boolean>();
  protected readonly displayScreenFields = signal<ScreenFieldModel[]>([]);
  readonly tilesRef = viewChildren<ElementRef>('tileRef');

  tiles: CalendarTile[] = [];
  maxTileDocs = 1;
  isLoading: boolean;
  startDate: Date;
  endDate: Date;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private loadData$ = new BehaviorSubject<boolean>(false);
  private dataListFields: DataListFieldPropertiesModel[];
  private descDataListField: DataListFieldPropertiesModel;
  private dateDataListField: DataListFieldPropertiesModel;
  private descTableColumns: DescTableColumn[];
  private tileTitleHeightPx = 26;
  private tileMoreHeightPx = 34;
  private tileDocHeightPx = 20;

  constructor() {
    this.loadData$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(load => {
      if (load) {
        this.getData();
      }
    });

    this.localeService.localeChanged.pipe(takeUntil(this.destroy$)).subscribe(() => this.loading(true));
  }

  abstract getTiles(empty: boolean, docs?: GridSearchEntityInstance[]): Observable<CalendarTile[]>;

  abstract getTileDocs(tile: CalendarTile, docs: CalendarTileDoc[]): CalendarTileDoc[];

  onResize() {
    this.loading(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.screenFields) {
      this.displayScreenFields.set(this.screenFields() || []);
    }
    if (
      (changes.descField && changes.descField.currentValue) ||
      (changes.dateField && changes.dateField.currentValue)
    ) {
      this.getDataListFields()
        .pipe(
          tap(fieldsResult => {
            this.descDataListField = fieldsResult.find(f => f.id == this.descField()?.id);
            this.dateDataListField = fieldsResult.find(f => f.id == this.dateField()?.id);
            this.enrichScreenFieldsRestrictions(fieldsResult);
          }),
          switchMap(() => this.loadDescTableColumns()),
        )
        .subscribe(() => this.loading(true));
    } else {
      if (changes.screenFields && this.dataListFields) {
        this.enrichScreenFieldsRestrictions(this.dataListFields);
      }
      this.loading(true);
    }
  }

  /** The card layout (`screenFields`) is a snapshot persisted whenever the card was last
   *  configured, so its restrictions/formattings can go stale if a field's configuration changes
   *  afterwards. Overlay the live field properties (fetched fresh here) so formatting (e.g. Date
   *  vs DateTime vs Time) always reflects the field's current configuration. */
  private enrichScreenFieldsRestrictions(dataListFields: DataListFieldPropertiesModel[]): void {
    const screenFields = this.screenFields() || [];
    if (!screenFields.length || !dataListFields?.length) {
      this.displayScreenFields.set(screenFields);
      return;
    }
    this.displayScreenFields.set(
      screenFields.map(field => {
        const liveField = dataListFields.find(f => f.id == field.id);
        if (!liveField) {
          return field;
        }
        return { ...field, restrictions: liveField.restrictions, formattings: liveField.formattings };
      }),
    );
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  showTileDocs(docs: CalendarTileDoc[]): CalendarTileDoc[] {
    const m = this.getMoreTileDocsNumber(docs);
    const reducedDocs = m > 0 ? (docs || []).slice(0, this.maxTileDocs) : docs || [];
    return reducedDocs;
  }

  getMoreTileDocsNumber(docs: CalendarTileDoc[]): number {
    const n = (docs || []).length - this.maxTileDocs;
    return n > 1 ? n : 0;
  }

  more(tile: CalendarTile, moreButton: any): void {
    // find and close all doc detail dialogs
    const differentDocDetailDialogs = this.dialog.openDialogs.filter(
      d => d.id && d.id.startsWith(CalendarDialogDocDetailPrefix),
    );
    for (const dialog of differentDocDetailDialogs) {
      dialog.close();
    }

    // find and close all more dialogs, we will keep only one open
    const id = `${CalendarDialogMorePrefix}-${moment(tile.date).format('YYYY-MM-DD')}`;
    const differentMoreDialogs = this.dialog.openDialogs.filter(
      d => d.id && d.id.startsWith(CalendarDialogMorePrefix) && d.id != id,
    );
    for (const dialog of differentMoreDialogs) {
      dialog.close();
    }

    // open a new more dialog
    this.openDialog(tile, moreButton);
  }

  protected processDocs(viewMode: CalendarViewMode, docs: GridSearchEntityInstance[]): CalendarTileDoc[] {
    const data = (docs || []).map(d => {
      const dt = this.processDate(d);
      if (!dt) {
        return undefined;
      }

      // generate new state
      const state = this.tableDataService.mapState(d);
      // set state to have it in the future
      d.state = state;

      return {
        stateColor: state.bgColor ?? (d.entity_template_id ? PRESET_BG_COLOR : d.state_color),
        ...this.formatDescField(d),
        time: this.formatTime(dt, viewMode),
        currency: this.currency(),
        ref: {
          ...d,
          date: dt.toDate(),
          year: dt.year(),
          month: dt.month(),
          day: dt.date(),
        },
      } as CalendarTileDoc;
    });
    return data.filter(d => !!d).sort((a, b) => a.ref!.date!.getTime() - b.ref!.date!.getTime());
  }

  private loading(value: boolean) {
    setTimeout(() => {
      this.loadData$.next(value);
      this.isLoading = value;
      this.onLoading.emit(value);
    }, 0);
  }

  private processDate(d: GridSearchEntityInstance): moment.Moment | undefined {
    let dateString = this.getFieldValue(d, this.dateField());
    if (!dateString || !this.dateDataListField) {
      return undefined;
    }

    const datetimeFormatting = (this.dateDataListField.restrictions || []).find(f => f.key == RestrictionType.DateTime);
    const handleTimezone = !datetimeFormatting || datetimeFormatting.value == RestrictionValueType.DateTime;

    const hasTimezone = dateString.endsWith('Z');
    if (handleTimezone && !hasTimezone) {
      dateString = dateString + 'Z';
    } else if (!handleTimezone && hasTimezone) {
      dateString = dateString.slice(0, -1);
    }

    const dt = moment(dateString);

    if (!dt.isValid()) {
      return undefined;
    }

    return dt;
  }

  private getDataListFields(): Observable<DataListFieldPropertiesModel[]> {
    if (this.dataListFields) {
      return of(this.dataListFields);
    }
    return this.calendarService.getDataListFields().pipe(
      map(fieldsResult => {
        this.dataListFields = fieldsResult.data;
        return this.dataListFields;
      }),
    );
  }

  private loadDescTableColumns(): Observable<void> {
    this.descTableColumns = undefined;
    const isTableLike =
      this.descDataListField?.typeId == FieldTypeEnum.Table || this.descDataListField?.typeId == FieldTypeEnum.Weather;
    if (!isTableLike || !this.descField()?.id) {
      return of(undefined);
    }
    /* Weather fields are backed by the same table-field column infrastructure (fixed well-known
       column names, dynamic labels/order/restrictions), so the same endpoint fetches both. */
    return this.fieldDefinitionService.getTableFieldColumns(this.descField().id).pipe(
      map(columns => {
        this.descTableColumns = (columns || []).slice().sort((a, b) => a.position - b.position);
        return undefined;
      }),
    );
  }

  private getData() {
    if (!this.loadData$.value) {
      return;
    }

    this.maxTileDocs = 1;

    this.getTiles(true)
      .pipe(
        tap((tiles: CalendarTile[]) => {
          this.tiles = tiles;

          const field = this.getFieldId(this.dateField()) || this.getFieldName(this.dateField());
          if (!field) {
            this.loading(false);
            return;
          }

          this.calendarService
            .getData(this.contractId(), field, this.startDate, this.endDate, this.search())
            .pipe(
              switchMap((result: GridViewModel<GridSearchEntityInstance[]>) => this.getTiles(false, result.data)),
              tap((tiles: CalendarTile[]) => (this.tiles = tiles)),
            )
            .subscribe(() => {
              this.getTileHeight();
              this.loading(false);
            });
        }),
      )
      .subscribe();
  }

  private getFieldId(field: CalendarReferenceField) {
    return `${field?.id || ''}`;
  }

  private getFieldName(field: CalendarReferenceField) {
    return field?.name;
  }

  private getFieldValue(d: GridSearchEntityInstance, field: CalendarReferenceField): string {
    return d[this.getFieldId(field)] || d[this.getFieldName(field)] || '';
  }

  private formatTime(dt: moment.Moment, viewMode: CalendarViewMode): string {
    let time = '';
    if (viewMode == CalendarViewMode.Year) {
      time = `${dt.format('DD')} | `;
    } else if (viewMode == CalendarViewMode.Month) {
      const ftime = dt.format('HH:mm');
      time = ftime == '00:00' ? '' : ftime;
    }
    return time;
  }

  private formatDescField(
    d: GridSearchEntityInstance,
  ): Pick<CalendarTileDoc, 'desc' | 'descMoreRowsCount' | 'descTableColumns' | 'descTableRows'> {
    const prefix = d.full_doc_num;
    const descString = this.getFieldValue(d, this.descField());
    if (!this.descField() || !descString) {
      return { desc: prefix };
    }
    if (this.descDataListField.typeId == FieldTypeEnum.Date) {
      const dtValue = this.itvDateTimePipe.transform(descString, this.descDataListField.restrictions);
      return { desc: this.formatDescFieldValueString(prefix, dtValue) };
    } else if (this.descDataListField.typeId == FieldTypeEnum.Number) {
      const currency = this.currency();
      return {
        desc: this.itvNumberPipe.transform(
          descString,
          this.descDataListField.formattings,
          this.descDataListField.restrictions,
          typeof currency == 'function' ? currency(d) : currency,
        ),
      };
    } else if (
      this.descDataListField.typeId == FieldTypeEnum.Table ||
      this.descDataListField.typeId == FieldTypeEnum.Weather
    ) {
      return this.formatDescFieldTableValue(prefix, descString, d);
    }
    return { desc: this.formatDescFieldValueString(prefix, descString) };
  }

  private formatDescFieldTableValue(
    prefix: string,
    descString: string,
    d: GridSearchEntityInstance,
  ): Pick<CalendarTileDoc, 'desc' | 'descMoreRowsCount' | 'descTableColumns' | 'descTableRows'> {
    const rawRows = parseTableFieldValue(descString);
    if (!rawRows.length) {
      return { desc: prefix };
    }

    const columns = this.descTableColumns;
    const rows = rawRows.map(row => row.map((value, i) => this.formatTableCellValue(value, columns?.[i], d)));
    const [firstRow, ...restRows] = rows;

    return {
      desc: this.formatDescFieldValueString(prefix, firstRow.join(', ')),
      descMoreRowsCount: restRows.length || undefined,
      descTableColumns: columns?.map(c => c.label || c.name),
      descTableRows: rows,
    };
  }

  private formatTableCellValue(value: string, column: DescTableColumn, d: GridSearchEntityInstance): string {
    if (!value || !column) {
      return value;
    }
    const currency = this.currency();
    if (column.type == FieldTypeEnum.Date) {
      return this.itvDateTimePipe.transform(value, column.restrictions) || value;
    }
    if (column.type == FieldTypeEnum.Number) {
      return this.itvNumberPipe.transform(
        value,
        column.formattings,
        column.restrictions,
        typeof currency == 'function' ? currency(d) : currency,
      );
    }
    return value;
  }

  private formatDescFieldValueString(prefix: string, descString: string): string {
    return descString ? `${prefix} - ${descString}` : prefix;
  }

  private getTileHeight() {
    const tiles = this.tilesRef();
    if (!tiles.length) return;
    const tile = tiles[0].nativeElement;
    this.maxTileDocs = Math.floor(
      (tile.clientHeight - this.tileTitleHeightPx - this.tileMoreHeightPx) / this.tileDocHeightPx,
    );
  }

  private openDialog(tile: CalendarTile, moreButton: any) {
    const moreButtonRect: DOMRect = moreButton._elementRef.nativeElement.getBoundingClientRect();
    const parentRect: DOMRect = document.body.getBoundingClientRect();

    const width = CalendarDialogMoreOuterWidth,
      height = CalendarDialogMoreOuterHeight,
      margin = 8;
    let top = moreButtonRect.top;
    let left = moreButtonRect.left;

    if (top + height > parentRect.bottom) {
      top = parentRect.bottom - height - margin;
    }
    if (left + width > parentRect.right) {
      left = parentRect.right - width - margin;
    }

    this.dialog.open(CalendarDialogMoreComponent, {
      data: {
        tile: tile,
        screenFields: this.displayScreenFields(),
        actions: this.actions(),
      },
      hasBackdrop: false,
      id: `${CalendarDialogMorePrefix}-${moment(tile.date).format('YYYY-MM-DD')}`,
      position: {
        top: `${top}px`,
        left: `${left}px`,
      },
    });
  }
}
